import React, { useState } from 'react';
import { Mail, Phone, UserRound } from 'lucide-react';

/**
 * Owner names, numbers and emails on a re-listing opportunity, from the
 * office's spreadsheets (Christian Gravias, Monday 13354971743): "Expired
 * listings" (22 Oct 2026) and "ADD DATA INTO CRM" (24 Oct: vendor enquiries,
 * landlords, investors, blocks, past sales), likeliest owner first. Shows the
 * first contact; "+N more" opens the rest. Add Lead pre-fills from the first
 * two. The Public Property Search page lists them with OwnerContactList.
 *
 * 27 Oct 2026 (same item): "Contact relationships are labelled accurately and
 * not automatically treated as verified ownership". Each person is tagged with
 * their relationship (Reported owner, Landlord / rental provider, Historical
 * contact), and the full list says "Not verified" and which file it came from.
 */

const stop = (e) => e.stopPropagation();

// The "sheet" the database team's own entries are filed under
// (relisting_sheet.CRM_ENTERED), from the row's Edit form.
export const CRM_ENTERED = 'ENTERED IN CRM';

// Who the person is to the property, when it is not simply "Owner".
const ROLE_STYLE = {
  'Current landlord': 'bg-sky-100 text-sky-700',
  'Bought through Collings': 'bg-emerald-100 text-emerald-700',
  'Block owner': 'bg-indigo-100 text-indigo-700',
  Investor: 'bg-amber-100 text-amber-700',
  'Vendor enquiry': 'bg-purple-100 text-purple-700',
  'CVMT lead': 'bg-rose-100 text-rose-700',
  'Sold through Collings': 'bg-gray-200 text-gray-600',
};
const ROLE_LABEL = { 'Sold through Collings': 'Sold through Collings (previous owner)' };

const RELATIONSHIP_STYLE = {
  registered_owner: 'bg-emerald-100 text-emerald-700',
  reported_owner: 'bg-teal-100 text-teal-800',
  landlord: 'bg-sky-100 text-sky-700',
  historical: 'bg-gray-200 text-gray-600',
  occupant: 'bg-amber-100 text-amber-700',
};
// The role already said by the relationship, so not repeated after it.
const SAME_AS_RELATIONSHIP = { Owner: true, 'Current landlord': true, 'Sold through Collings': true };

const unverified = (c) => c.verification && c.verification !== 'verified';

const RoleTag = ({ c }) => {
  const { role } = c;
  if (c.relationship_label) {
    const extra = role && !SAME_AS_RELATIONSHIP[role] ? role : '';
    const text = c.relationship === 'historical' && role === 'Sold through Collings'
      ? `${c.relationship_label} · sold through Collings`
      : [c.relationship_label, extra].filter(Boolean).join(' · ');
    return (
      <span
        title={unverified(c) ? 'Not verified: from our own records, not a title search' : undefined}
        className={`rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wide whitespace-nowrap ${
          RELATIONSHIP_STYLE[c.relationship] || 'bg-gray-100 text-gray-600'
        }`}
      >
        {text}
      </span>
    );
  }
  return role && role !== 'Owner' ? (
    <span
      className={`rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wide whitespace-nowrap ${
        ROLE_STYLE[role] || 'bg-gray-100 text-gray-600'
      }`}
    >
      {ROLE_LABEL[role] || role}
    </span>
  ) : null;
};

// "26/10/2026" from "2026-10-26".
const dmy = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

// Where the record came from, for the full list: tab, file and import date.
const sourceText = (c) => {
  if (!c.sheet) return '';
  if (c.sheet === CRM_ENTERED) return 'Entered in CRM';
  const file = c.source_file ? ` (${c.source_file})` : '';
  const when = c.imported_at ? `, imported ${dmy(c.imported_at)}` : '';
  return `From ${c.sheet}${file}${when}`;
};

const ContactLine = ({ c, showSource = false }) => {
  const who = c.contact_name || c.owner_name;
  // The title holder, when the person on the phone is someone else.
  const onTitle = c.contact_name && c.owner_name && c.owner_name.toLowerCase() !== c.contact_name.toLowerCase()
    ? c.owner_name
    : '';
  const source = showSource ? sourceText(c) : '';
  const verified = showSource && unverified(c) ? c.verification_label || 'Not verified' : '';
  const details = [onTitle && `On title: ${onTitle}`, verified, source].filter(Boolean).join(' · ');
  return (
    <div className="py-1 first:pt-0 last:pb-0 border-b border-teal-100/60 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
        <span className="inline-flex items-center gap-1 font-semibold text-gray-800 min-w-0">
          <UserRound className="w-3 h-3 text-teal-700 flex-shrink-0" />
          <span className="truncate">{who || 'Owner not named'}</span>
        </span>
        <RoleTag c={c} />
        {(c.phones || []).map((p) => (
          <a
            key={p}
            href={`tel:${p.replace(/\s+/g, '')}`}
            onClick={stop}
            className="inline-flex items-center gap-1 font-semibold text-teal-700 hover:underline whitespace-nowrap"
          >
            <Phone className="w-3 h-3" /> {p}
          </a>
        ))}
        {(c.emails || (c.email ? [c.email] : [])).map((e) => (
          <a
            key={e}
            href={`mailto:${e}`}
            onClick={stop}
            className="inline-flex items-center gap-1 text-gray-600 hover:text-teal-600 hover:underline min-w-0"
          >
            <Mail className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{e}</span>
          </a>
        ))}
        {c.door_knock && !(c.phones || []).length && <span className="text-gray-500">No number found, door knock</span>}
      </div>
      {(details || c.notes) && (
        <div className="text-[10px] text-gray-500 leading-snug mt-0.5 break-words">
          {details && <span>{details}</span>}
          {details && c.notes && <span> · </span>}
          {c.notes && <span className="whitespace-pre-line">{c.notes}</span>}
        </div>
      )}
    </div>
  );
};

// Every contact, with the spreadsheet tab each came from (the Public Property Search page).
export const OwnerContactList = ({ contacts }) => (
  <div>
    {(contacts || []).map((c) => (
      <ContactLine key={c.id} c={c} showSource />
    ))}
  </div>
);

/*
 * The agents' own people for the property (Christian, Monday 13354971743,
 * 28 Oct 2026: "All our data has to speak to eachother"): an owner row in a
 * Database the viewer may open, or a seller lead on the Prospects board. A
 * colleague's private Database only shows as "In Paul Jimoulis's Database".
 */
const SOURCE_TAG = {
  database: { text: 'Database', cls: 'bg-teal-100 text-teal-800' },
  pipeline: { text: 'Pipeline', cls: 'bg-fuchsia-100 text-fuchsia-700' },
};

const crmWhere = (p) =>
  p.source === 'pipeline'
    ? [p.stage_label, p.agent_name].filter(Boolean).join(' · ')
    : [p.agent_name && `${p.agent_name}'s Database`, p.list_name].filter(Boolean).join(' · ');

export const CrmPersonLine = ({ p, onOpen }) => {
  const tag = SOURCE_TAG[p.source] || SOURCE_TAG.database;
  const where = crmWhere(p);
  return (
    <div className="py-1 first:pt-0 last:pb-0 border-b border-teal-100/60 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
        <span className="inline-flex items-center gap-1 font-semibold text-gray-800 min-w-0">
          <UserRound className="w-3 h-3 text-teal-700 flex-shrink-0" />
          <span className="truncate">{[p.name, p.second_name].filter(Boolean).join(' & ') || 'Owner not named'}</span>
        </span>
        <span className={`rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wide whitespace-nowrap ${tag.cls}`}>
          {tag.text}
        </span>
        {(p.phones || []).map((ph) => (
          <a
            key={ph}
            href={`tel:${ph.replace(/\s+/g, '')}`}
            onClick={stop}
            className="inline-flex items-center gap-1 font-semibold text-teal-700 hover:underline whitespace-nowrap"
          >
            <Phone className="w-3 h-3" /> {ph}
          </a>
        ))}
        {(p.emails || []).map((e) => (
          <a
            key={e}
            href={`mailto:${e}`}
            onClick={stop}
            className="inline-flex items-center gap-1 text-gray-600 hover:text-teal-600 hover:underline min-w-0"
          >
            <Mail className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{e}</span>
          </a>
        ))}
      </div>
      {(where || p.last_note) && (
        <div className="text-[10px] text-gray-500 leading-snug mt-0.5 break-words">
          {where &&
            (onOpen && p.url ? (
              <button
                type="button"
                onClick={(e) => {
                  stop(e);
                  onOpen(p.url);
                }}
                className="font-semibold text-teal-700 hover:underline"
              >
                {where}
              </button>
            ) : (
              <span>{where}</span>
            ))}
          {where && p.last_note && <span> · </span>}
          {p.last_note && <span className="whitespace-pre-line">Last note: {p.last_note}</span>}
        </div>
      )}
    </div>
  );
};

// "In Paul Jimoulis's Database": a colleague's private Database holds this property (names only).
export const HeldByLine = ({ heldBy }) =>
  heldBy && heldBy.length ? (
    <p className="text-[10px] text-gray-500 leading-snug">
      Also in {heldBy.map((n) => `${n}'s`).join(', ')} Database. Ask them to share it to see the details.
    </p>
  ) : null;

const RelistingOwnerContacts = ({ contacts, people = [], heldBy = [] }) => {
  const [showAll, setShowAll] = useState(false);
  const all = [
    ...(contacts || []).map((c) => ({ kind: 'sheet', key: `s-${c.id}`, c })),
    ...(people || []).map((p) => ({ kind: 'crm', key: `${p.source}-${p.id}`, c: p })),
  ];
  if (!all.length && !(heldBy || []).length) return null;
  const shown = showAll ? all : all.slice(0, 1);
  const more = all.length - 1;
  return (
    <div className="mt-2 ml-12 rounded-lg bg-teal-50/60 px-3 py-2" onClick={stop}>
      {shown.map((item) =>
        item.kind === 'sheet' ? <ContactLine key={item.key} c={item.c} /> : <CrmPersonLine key={item.key} p={item.c} />
      )}
      {(showAll || more <= 0) && <HeldByLine heldBy={heldBy} />}
      {more > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((s) => !s)}
          aria-expanded={showAll}
          className="mt-1 text-[10px] font-semibold text-teal-700 hover:underline"
        >
          {showAll ? 'Show less' : `+${more} more ${more === 1 ? 'contact' : 'contacts'} on file`}
        </button>
      )}
    </div>
  );
};

export default RelistingOwnerContacts;
